import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type {
  CalendarAgendaResult,
  CalendarGridResult,
  CalendarStatsResult,
  EventDetail,
} from "@/components/calendar/calendarApiMappers";
import type {
  CalendarEvent,
  CalendarStats,
  FilterOption,
} from "@/components/calendar/types";

/**
 * Calendar feature state (Figma 3.1 / 3.2), hydrated from the three calendar
 * read endpoints. `month` is a serializable `YYYY-MM` string; filter selections
 * are option values (`all` or a UUID / range slug).
 */
export interface CalendarState {
  // Grid
  month: string;
  events: CalendarEvent[];
  spaceOptions: FilterOption[];
  eventTypeOptions: FilterOption[];
  space: string; // "all" or a space UUID
  eventType: string; // "all" or an event-type UUID
  gridLoading: boolean;
  // Stats
  stats: CalendarStats | null;
  rangeOptions: FilterOption[];
  rangeSlug: string;
  statsLoading: boolean;
  // Agenda rail
  todayEvents: CalendarEvent[];
  upcomingEvents: CalendarEvent[];
  // Detail panel toggle
  selectedEvent: CalendarEvent | null;
  loading: boolean;
  /**
   * Fetched event details memoized by `bookingId:eventId`, so re-opening (or a
   * hover-prefetched) event renders instantly with no refetch. `undefined` = not
   * loaded, `null` = fetched-but-empty.
   */
  eventDetails: Record<string, EventDetail | null>;
}

/** Current month as `YYYY-MM` — a safe default before hydration. */
function currentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

const initialState: CalendarState = {
  month: currentMonth(),
  events: [],
  spaceOptions: [],
  eventTypeOptions: [],
  space: "all",
  eventType: "all",
  gridLoading: false,
  stats: null,
  rangeOptions: [],
  rangeSlug: "this_month",
  statsLoading: false,
  todayEvents: [],
  upcomingEvents: [],
  selectedEvent: null,
  loading: true,
  eventDetails: {},
};

/** One-shot payload the Initializer hydrates from the SSR fetchers. */
export interface CalendarHydratePayload {
  grid: CalendarGridResult;
  stats: CalendarStatsResult;
  agenda: CalendarAgendaResult;
  rangeSlug: string;
}

const calendarSlice = createSlice({
  name: "calendar",
  initialState,
  reducers: {
    hydrateCalendar(state, action: PayloadAction<CalendarHydratePayload>) {
      // Defensive: tolerate a partial payload (e.g. a stale build mid-HMR)
      // rather than crashing the page.
      const { grid, stats, agenda, rangeSlug } = action.payload ?? {};
      if (grid) {
        state.month = grid.month || state.month;
        state.events = grid.events ?? [];
        state.spaceOptions = grid.spaceOptions ?? [];
        state.eventTypeOptions = grid.eventTypeOptions ?? [];
      }
      if (stats) {
        state.stats = stats.stats;
        state.rangeOptions = stats.rangeOptions ?? [];
      }
      if (agenda) {
        state.todayEvents = agenda.today ?? [];
        state.upcomingEvents = agenda.upcoming ?? [];
      }
      if (rangeSlug) state.rangeSlug = rangeSlug;
    },
    /** Replace the grid (events + month) after a month/filter refetch. */
    setGrid(state, action: PayloadAction<CalendarGridResult>) {
      const grid = action.payload;
      if (!grid) return;
      state.month = grid.month || state.month;
      state.events = grid.events ?? [];
      // Options are stable; only refresh them if the payload carries some.
      if (grid.spaceOptions?.length) state.spaceOptions = grid.spaceOptions;
      if (grid.eventTypeOptions?.length) {
        state.eventTypeOptions = grid.eventTypeOptions;
      }
      state.selectedEvent = null;
    },
    /** Replace the stats after a range-filter refetch. */
    setStats(state, action: PayloadAction<CalendarStatsResult>) {
      const payload = action.payload;
      if (!payload) return;
      state.stats = payload.stats;
      if (payload.rangeOptions?.length) {
        state.rangeOptions = payload.rangeOptions;
      }
    },
    setMonth(state, action: PayloadAction<string>) {
      state.month = action.payload;
    },
    setSpaceFilter(state, action: PayloadAction<string>) {
      state.space = action.payload;
    },
    setEventTypeFilter(state, action: PayloadAction<string>) {
      state.eventType = action.payload;
    },
    setRangeSlug(state, action: PayloadAction<string>) {
      state.rangeSlug = action.payload;
    },
    setGridLoading(state, action: PayloadAction<boolean>) {
      state.gridLoading = action.payload;
    },
    setStatsLoading(state, action: PayloadAction<boolean>) {
      state.statsLoading = action.payload;
    },
    selectEvent(state, action: PayloadAction<CalendarEvent>) {
      state.selectedEvent = action.payload;
    },
    clearSelectedEvent(state) {
      state.selectedEvent = null;
    },
    /**
     * Memoize a fetched event detail keyed by `bookingId:eventId` so re-opening
     * (or hover-prefetching) an event is instant — no refetch. `null` = fetched
     * but empty (still counts as loaded, so the panel stops showing placeholders).
     */
    cacheEventDetail(
      state,
      action: PayloadAction<{ key: string; detail: EventDetail | null }>,
    ) {
      state.eventDetails[action.payload.key] = action.payload.detail;
    },
    setCalendarLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
  },
});

export const {
  hydrateCalendar,
  setGrid,
  setStats,
  setMonth,
  setSpaceFilter,
  setEventTypeFilter,
  setRangeSlug,
  setGridLoading,
  setStatsLoading,
  selectEvent,
  clearSelectedEvent,
  cacheEventDetail,
  setCalendarLoading,
} = calendarSlice.actions;

export default calendarSlice.reducer;
